import styles from './BookedState.module.scss';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { JoinState } from '../../api/bookingApi.types';

interface JoinSectionProps {
  joinState: JoinState;
  /** Present only while `joinState` is `available`: the server withholds it afterwards. */
  joinUrl: string | null;
}

/**
 * Where the candidate joins the interview. The link is shown only when the
 * API gives one, so an interview that has ended can never show it however
 * long the page stays open.
 */
const JoinSection: FC<JoinSectionProps> = ({ joinState, joinUrl }) => {
  const { t } = useTranslation();

  if (joinState === JoinState.ENDED) {
    return (
      <p className={styles.note}>{t('publicScheduling.interviewEnded')}</p>
    );
  }

  const content: ReactNode =
    joinState === JoinState.AVAILABLE && joinUrl !== null ? (
      <>
        <a
          className={styles.link}
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('publicScheduling.joinInterview')}
        </a>
        <p className={styles.note}>{t('publicScheduling.lobbyNote')}</p>
      </>
    ) : joinState === JoinState.PENDING ? (
      <p className={styles.note}>{t('publicScheduling.joinPending')}</p>
    ) : null;

  if (content === null) {
    return null;
  }

  return (
    <section className={styles.panel} aria-labelledby="booked-join">
      <h2 id="booked-join" className={styles.panelTitle}>
        {t('publicScheduling.joinTitle')}
      </h2>
      {content}
    </section>
  );
};

export default JoinSection;
